import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from '@/common/components/Helmet'
import Survey from '@/features/surveys/components/Survey'
import { useSurvey } from '../route'
import { isValidLanguageId, languageObject } from '@/common/classes/LanguageUtils'
import { titleTagSuffix } from '@/common/constants'
import { localizedContent } from '@/features/surveys/classes/SurveyUtils'
import { organizationDisplayName } from '@/common/classes/OrganizationUtils'
import { Store } from '@/store/Store'

export const Route = createFileRoute(
  '/app/surveys/$surveyOrganizationUuid/$languageId/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { surveyOrganizationUuid, languageId } = Route.useParams()
  const { survey, demographicOptions } = useSurvey()

  const [effectiveLanguageId, setEffectiveLanguageId] = useState<string | null>(
    null,
  )
  const [localizedSurveyName, setLocalizedSurveyName] =
    useState<string>('Survey')

  const languages = useSelector((state: Store) => state.app_meta.data.languages)

  // Set effectiveLanguageId
  useEffect(() => {
    let newELID = 'en'
    if (isValidLanguageId(languages, languageId)) {
      let langObj = languageObject(languages, languageId)
      if (langObj) {
        newELID = langObj.id!
      }
    }
    setEffectiveLanguageId(newELID)
  }, [languageId, languages])

  // Set localized name
  useEffect(() => {
    if (languages && effectiveLanguageId && survey) {
      setLocalizedSurveyName(
        localizedContent(languages, survey, 'name', effectiveLanguageId),
      )
    }
  }, [effectiveLanguageId, languages, survey])

  if (!effectiveLanguageId) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>
          {organizationDisplayName(survey.organization)}: {localizedSurveyName}
          {titleTagSuffix}
        </title>
        <meta
          property="og:title"
          content={`${organizationDisplayName(survey.organization)}: ${localizedSurveyName}`}
        />
        <meta
          property="og:url"
          content={`https://${import.meta.env.VITE_LOCAL_HOSTNAME}/app/surveys/${surveyOrganizationUuid}/${effectiveLanguageId}`}
        />
      </Helmet>

      <Survey
        demographicOptions={demographicOptions}
        languageId={effectiveLanguageId}
        readOnly={false}
        survey={survey}
      />
    </>
  )
}
